/** 내기용 제비뽑기·사다리타기의 순수 로직. React 도 API 도 모른다.
 *
 * 서버를 쓰지 않는다. 뽑기는 이 브라우저 안에서 일어나고 아무것도 저장하지 않는다 —
 * 결과는 텍스트로 복사해서 단톡방에 붙인다.
 *
 * 그래서 **공정성은 소프트웨어가 아니라 자리가 보장한다**: 다 같이 한 화면을 보면서
 * 뽑는 그 순간이 근거다. 뽑는 사람이 혼자 여러 번 돌린 뒤 마음에 드는 것만 공유하는
 * 건 이 구조로 막을 수 없다(막으려면 서버가 결과를 먼저 확정하고 나중에 공개하는
 * commit-reveal 이 필요하다 — 지금 넣지 않았다). 대신 소프트웨어가 책임질 수 있는
 * 부분, 즉 **한 번의 뽑기가 편향되지 않는 것**은 확실히 한다.
 */

/** uint32 하나를 주는 난수원. 테스트가 결정적으로 돌 수 있게 주입받는다. */
export type RandomSource = () => number;

const UINT32_RANGE = 0x100000000;

export function cryptoSource(): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0];
}

/** 0 이상 `bound` 미만의 정수. 거부 표집이라 편향이 없다.
 *
 * `source() % bound` 로 끝내면 2^32 가 bound 로 나누어떨어지지 않는 한 앞쪽 값이
 * 더 자주 나온다. 내기에서 그 편향은 "먼저 선택된 사람이 더 잘 걸린다" 로 나타난다 —
 * 눈에 보이지 않으니 아무도 모른 채로 쓰게 된다. 몇 줄 더 쓰고 없앤다.
 */
export function randomBelow(bound: number, source: RandomSource = cryptoSource): number {
  if (bound <= 0) throw new Error("bound must be positive");
  // 균등하게 쓸 수 있는 구간의 끝. 이 위로 나온 값은 버리고 다시 뽑는다.
  const limit = Math.floor(UINT32_RANGE / bound) * bound;
  let value = source();
  while (value >= limit) value = source();
  return value % bound;
}

/** Fisher-Yates. `sort(() => Math.random() - 0.5)` 를 쓰지 않는다 — 그건 섞이지도
 *  않고 비교 함수 계약도 깨서 엔진마다 결과가 다르다. */
export function shuffle<T>(items: readonly T[], source: RandomSource = cryptoSource): T[] {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i--) {
    const j = randomBelow(i + 1, source);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/** 제비뽑기: 후보에서 `count` 명을 뽑는다. count 가 후보 수보다 크면 전원. */
export function drawWinners(
  names: readonly string[],
  count: number,
  source: RandomSource = cryptoSource
): string[] {
  return shuffle(names, source).slice(0, Math.max(0, Math.min(count, names.length)));
}

export const BLANK_OUTCOME = "꽝";

/** 사다리 아래에 놓을 결과 칸을 사람 수에 맞춘다.
 *
 * 모자라면 `꽝` 으로 채운다 — "커피" 한 줄만 적고 5명이 참여하는 게 실제로 가장
 * 흔한 사용법이라, 나머지를 손으로 적게 하지 않는다. 남으면 자른다.
 */
export function padOutcomes(outcomes: readonly string[], size: number): string[] {
  const kept = outcomes.map((o) => o.trim()).filter(Boolean).slice(0, size);
  return [...kept, ...Array(Math.max(0, size - kept.length)).fill(BLANK_OUTCOME)];
}

/** 한 줄의 가로줄들. 각 값은 "이 칸과 바로 오른쪽 칸을 잇는다" 는 뜻의 왼쪽 인덱스. */
export type LadderRow = number[];

export type Ladder = {
  /** 시작 위치 i 에 선 사람이 도착하는 위치. */
  permutation: number[];
  rows: LadderRow[];
};

/** 겹치지 않는 가로줄을 무작위로 깐 한 줄. 왼쪽부터 훑으며 절반쯤 놓고 한 칸 건너뛴다
 *  — 가로줄이 두 칸을 차지하므로 건너뛰지 않으면 한 기둥에 둘이 붙는다. */
function randomRow(size: number, source: RandomSource): LadderRow {
  const row: LadderRow = [];
  for (let left = 0; left + 1 < size; left++) {
    if (randomBelow(2, source) === 1) {
      row.push(left);
      left++;
    }
  }
  return row;
}

/** 목적지 배열을 인접 교환으로 정렬하면서 각 교환을 가로줄로 적는다.
 *
 * 홀짝 교환 정렬(odd-even transposition sort)이라 한 줄 안의 교환이 서로 겹치지 않고
 * (짝수 인덱스끼리 / 홀수 인덱스끼리) 줄 수도 size 이하로 끝난다.
 */
function sortingRows(destination: number[], size: number): LadderRow[] {
  const rows: LadderRow[] = [];
  for (let pass = 0; pass < size; pass++) {
    const row: LadderRow = [];
    for (let left = pass % 2; left + 1 < size; left += 2) {
      if (destination[left] > destination[left + 1]) {
        [destination[left], destination[left + 1]] = [destination[left + 1], destination[left]];
        row.push(left);
      }
    }
    if (row.length > 0) rows.push(row);
  }
  return rows;
}

/** 사다리를 만든다.
 *
 * 순서가 중요하다: **균등 순열을 먼저 뽑고, 그 순열을 실현하는 가로줄을 만든다.**
 * 가로줄을 무작위로 뿌린 뒤 따라가는 방식이 아니다 — 그렇게 하면 (1) 줄 수가 적을 때
 * 순열이 균등하지 않고 (2) 편향의 크기를 아무도 계산할 수 없다.
 *
 * 그런데 목표 순열만 최소 교환으로 실현하면, 뽑힌 순열이 항등에 가까울 때 6명에
 * 가로줄이 두 개뿐인 사다리가 나온다. 결과는 균등한데 화면은 안 섞인 것처럼 보이고,
 * 그 자리에서 "이거 제대로 돌아간 거야?" 가 나온다 — 이 기능은 보이는 게 절반이다.
 * 그래서 두 단계로 만든다:
 *
 * 1. 아무 뜻 없는 가로줄을 size 줄만큼 깐다(그림의 밀도).
 * 2. 그게 만들어 낸 배치에서 **목표 순열까지** 데려가는 보정 줄을 뒤에 붙인다.
 *
 * 전체 사상은 여전히 1단계에서 뽑은 균등 순열이다. 1단계가 무엇을 하든 2단계가
 * 그것을 흡수한다.
 *
 * 이렇게 하면 화면의 사다리와 결과가 **같은 출처**를 갖는다. 손가락으로 따라가면
 * 반드시 발표된 결과가 나온다 — 사람들은 실제로 따라가서 확인한다.
 */
export function buildLadder(size: number, source: RandomSource = cryptoSource): Ladder {
  if (size <= 1) return { permutation: size === 1 ? [0] : [], rows: [] };

  const permutation = shuffle(
    Array.from({ length: size }, (_, i) => i),
    source
  );

  const decoy: LadderRow[] = [];
  for (let i = 0; i < size; i++) {
    const row = randomRow(size, source);
    if (row.length > 0) decoy.push(row);
  }

  // decoy 를 지나면 출발 i 는 afterDecoy[i] 에 서 있다. 거기서부터 목적지를 적어
  // 정렬하면, decoy + 보정 = permutation 이 된다.
  const afterDecoy = traceLadder(size, decoy);
  const destination = new Array<number>(size);
  for (let start = 0; start < size; start++) {
    destination[afterDecoy[start]] = permutation[start];
  }

  return { permutation, rows: [...decoy, ...sortingRows(destination, size)] };
}

/** 가로줄을 실제로 따라가서 도착 위치를 낸다. `buildLadder` 의 permutation 과 같아야
 *  한다 — 그 불변식이 깨지면 화면과 결과가 어긋나므로 테스트가 지킨다. */
export function traceLadder(size: number, rows: readonly LadderRow[]): number[] {
  return Array.from({ length: size }, (_, start) => {
    let position = start;
    for (const row of rows) {
      if (row.includes(position)) position += 1;
      else if (row.includes(position - 1)) position -= 1;
    }
    return position;
  });
}

/** 한 사람이 줄을 따라 지나온 칸들. 길이는 `rows.length + 1` 이고 첫 값이 출발,
 *  마지막 값이 도착이다. 화면에 경로를 그리는 용도 — 그리는 선과 발표된 결과가
 *  갈라지지 않게 `traceLadder` 와 같은 규칙을 한 곳에서 쓴다. */
export function tracePath(rows: readonly LadderRow[], start: number): number[] {
  const path = [start];
  let position = start;
  for (const row of rows) {
    if (row.includes(position)) position += 1;
    else if (row.includes(position - 1)) position -= 1;
    path.push(position);
  }
  return path;
}

export type DrawAssignment = { name: string; outcome: string };

/** 사다리타기: 사람과 결과 칸을 1:1 로 잇는다. */
export function assignByLadder(
  names: readonly string[],
  outcomes: readonly string[],
  ladder: Ladder
): DrawAssignment[] {
  return names.map((name, start) => ({
    name,
    outcome: outcomes[ladder.permutation[start]] ?? BLANK_OUTCOME,
  }));
}

/** 단톡방에 그대로 붙여넣는 텍스트. `formatOrderText` 와 같은 용도다. */
export function formatWinnersText(title: string, winners: readonly string[]): string {
  return [`[제비뽑기] ${title}`, "", winners.join(", ")].join("\n");
}

export function formatLadderText(
  title: string,
  assignments: readonly DrawAssignment[]
): string {
  const lines = [`[사다리타기] ${title}`, ""];
  for (const { name, outcome } of assignments) lines.push(`${name} → ${outcome}`);
  return lines.join("\n");
}
